import {LABELS} from '../../shared/labels/Labels';
import {
  useCombinedCountryData,
  useFieldValueChangeMonitoring,
  usePortalLanguagesData,
  useSignUpTrackingEvents,
} from '../hooks';
import {useEmailAlreadyInUseQuery} from '../queries';
import {
  StyledContainer,
  StyledForm,
  FlexStartText,
  WideButton,
} from '../styled';
import {labels, validationLabels} from '../translations';
import {BaseFormProps, SignupForm} from '../types';
import {
  localizeDefaultData,
  usePhonePrefixOptions,
  validationRules,
} from '../utils';
import CountryFlagOption from './countryFlag/CountryFlagOption';
import CountryFlagSummary from './countryFlag/CountryFlagSummary';
import {Translate, useGlobalization} from '@planday-corp/globalization';
import {
  useValidation,
  validate as v,
  ValidationRules,
} from '@planday-corp/react-model';
import Link from '@planday-corp/ui/actions/Link';
import Grid from '@planday-corp/ui/forms/Grid';
import SelectField from '@planday-corp/ui/forms/fields/SelectField';
import TextField from '@planday-corp/ui/forms/fields/TextField';
import Icon from '@planday-corp/ui/icons/Icon';
import InlineSpinner from '@planday-corp/ui/indicators/InlineSpinner';
import Text from '@planday-corp/ui/text/Text';
import {useEffect, useState} from 'react';
import styled from 'styled-components';

const PREFIX_FIELD_WIDTH = '7.5rem';

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const GRID_GAP = '1rem';

const CustomCell = styled.div<{$width: string}>`
  flex: 0 0 calc(${props => props.$width} - ${GRID_GAP});
  width: calc(${props => props.$width} - ${GRID_GAP});
  margin-right: ${GRID_GAP};
`;

interface AccountForm {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  phoneNumber: number;
  termsAndConditions: boolean;
  marketingCommunications: boolean;
  phonePrefixOptions: string;
  phoneCountryCodePrefix: string;
  portalLanguageId: number;
}

const rules: ValidationRules<Partial<AccountForm>> = {
  email: [v.required({}), validationRules.email()],
  firstName: v.required(),
  lastName: v.required(),
  password: [v.required(), validationRules.password()],
  phoneNumber: [v.required(), validationRules.phone()],
  portalLanguageId: v.required(),
};

export default function AccountForm(props: BaseFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const {i18n} = useGlobalization();
  const {form, setStep, update} = props;
  const {l10n} = useGlobalization();

  const {data, isLoading: isLoadingEmailAlreadyInUse} =
    useEmailAlreadyInUseQuery(form.email);
  const {data: countriesPhoneData, isLoading: isLoadingCombinedData} =
    useCombinedCountryData();

  const isLoading = isLoadingEmailAlreadyInUse || isLoadingCombinedData;
  const emailErrorMessage = data?.isEmailUsed
    ? validationLabels.email.inUse
    : '';
  const track = useSignUpTrackingEvents();
  const {monitorValue, isValueDirty} = useFieldValueChangeMonitoring();

  const {portalLanguageOptions, portalLanguages} = usePortalLanguagesData();

  useEffect(() => {
    if (
      !form.companyName &&
      !form.phoneCountryCodePrefix &&
      !form.portalLanguageId
    ) {
      localizeDefaultData({countriesPhoneData, portalLanguages, update, l10n});
    }
  }, [countriesPhoneData, portalLanguages]);

  const {validate, errors} = useValidation(form, rules);

  const handleNextStep = () => {
    if (validate() && !isLoading && !emailErrorMessage) {
      update(true, 'termsAndConditions');
      track.next('business');
      setStep('business');
    }
  };

  const phonePrefixOptions = usePhonePrefixOptions(countriesPhoneData);

  const getTextWithLinks = () => {
    return (
      <Translate
        term={labels.termsAndConditions.term}
        fallback={labels.termsAndConditions.fallback}
        params={[
          <Link
            key="dataProcessing"
            href={i18n.getText(labels.dataProccesingLinks)}
            target="_blank"
            rel="noreferrer"
            color="blue20"
          >
            {i18n.getText(labels.dataProcessing)}
          </Link>,
          <Link
            key="privacyPolicy"
            href={i18n.getText(labels.privacyPolicyLinks)}
            target="_blank"
            rel="noreferrer"
            color="blue20"
          >
            {i18n.getText(labels.privacyPolicy)}
          </Link>,
          <Link
            key="trialTerms"
            href={i18n.getText(labels.trialTermsLinks)}
            target="_blank"
            rel="noreferrer"
            color="blue20"
          >
            {i18n.getText(labels.trialTerms)}
          </Link>,
          <Link
            key="generalTerms"
            href={i18n.getText(labels.generalTermsLinks)}
            target="_blank"
            rel="noreferrer"
            color="blue20"
          >
            {i18n.getText(labels.generalTerms)}
          </Link>,
        ]}
      />
    );
  };

  const onPhonePrefixChange = (value: string, name: string) => {
    update(value, name as keyof SignupForm);
    track.countryCode(value);
    const selectedCountry = countriesPhoneData.find(
      country => country.prefixNumber === value
    );
    if (selectedCountry) {
      update(selectedCountry.countryCode, 'countryCode');
    }
  };

  return (
    <>
      <FlexStartText size="title1" element="h1">
        {labels.accountStepTitle}
      </FlexStartText>
      <StyledForm>
        <TextField
          name={'firstName'}
          error={errors?.firstName}
          label={labels.firstName}
          isRequired={true}
          value={form.firstName}
          onFocus={() => monitorValue(form.firstName)}
          onBlur={() => isValueDirty(form.firstName) && track.firstName()}
          onChange={(value, name) => update(value, name as keyof SignupForm)}
        />
        <TextField
          name={'lastName'}
          error={errors?.lastName}
          label={labels.lastName}
          isRequired={true}
          value={form.lastName}
          onFocus={() => monitorValue(form.lastName)}
          onBlur={() => isValueDirty(form.lastName) && track.lastName()}
          onChange={(value, name) => update(value, name as keyof SignupForm)}
        />

        <TextField
          name={'email'}
          label={labels.email}
          error={errors?.email || emailErrorMessage}
          suffix={isLoading ? <InlineSpinner /> : undefined}
          isRequired={true}
          helpText={labels.emailSuggestion}
          value={form.email}
          onFocus={() => monitorValue(form.email)}
          onBlur={() => isValueDirty(form.email) && track.email()}
          onChange={update}
        />
        <Grid size={2}>
          <CustomCell $width={PREFIX_FIELD_WIDTH}>
            <SelectField
              isRequired
              isSearchable
              label={LABELS.phoneCountryCodePrefix}
              name="phoneCountryCodePrefix"
              options={phonePrefixOptions}
              value={form.phoneCountryCodePrefix || ''}
              error={
                errors?.phoneCountryCodePrefix || errors?.phonePrefixOptions
              }
              isLoading={isLoadingCombinedData}
              isDisabled={isLoadingCombinedData}
              dropdownMinWidth={300}
              renderOption={option => (
                <CountryFlagOption
                  option={option}
                  countriesPhoneData={countriesPhoneData}
                />
              )}
              renderSummary={({onClick}) => (
                <CountryFlagSummary
                  countriesPhoneData={countriesPhoneData}
                  onClick={onClick}
                  value={form.countryCode || ''}
                />
              )}
              onChange={onPhonePrefixChange}
            />
          </CustomCell>
          <CustomCell $width={`100% - ${PREFIX_FIELD_WIDTH}`}>
            <TextField
              name={'phoneNumber'}
              label={labels.phoneNumber}
              error={errors?.phoneNumber}
              isRequired={true}
              value={String(form.phoneNumber ?? '')}
              onFocus={() => monitorValue(form.phoneNumber)}
              onBlur={() =>
                isValueDirty(form.phoneNumber) && track.phoneNumber()
              }
              onChange={update}
            />
          </CustomCell>
        </Grid>
        <TextField
          name={'password'}
          label={labels.password}
          error={errors?.password}
          isRequired={true}
          value={form.password}
          onFocus={() => monitorValue(form.password)}
          onBlur={() => isValueDirty(form.password) && track.password()}
          suffix={
            <StyledIcon
              iconName={showPassword ? 'show' : 'hide'}
              onClick={() => setShowPassword(!showPassword)}
            />
          }
          type={showPassword ? 'text' : 'password'}
          onChange={update}
        />
        <SelectField
          name={'portalLanguageId'}
          error={errors?.portalLanguageId}
          value={form.portalLanguageId}
          isRequired={true}
          label={labels.portalLanguage}
          helpText={labels.portalLanguageHelpText}
          onChange={(value, name) => {
            track.portalLanguage(
              portalLanguages.find(language => language.id === value)
                ?.uiCultureName || ''
            );
            update(value, name);
          }}
          options={portalLanguageOptions}
        />
        <StyledContainer>
          <Text>{getTextWithLinks()}</Text>
        </StyledContainer>
      </StyledForm>
      <WideButton name="next" type="primary" onClick={handleNextStep}>
        <Translate {...labels.accountStepCTA} />
      </WideButton>
    </>
  );
}
